import _styled, { css } from 'styled-components';
import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { createStyles } from '@udecode/plate-styled-components';
import { toDOMRange, getNodeEntries, isText, toDOMNode, useEditorRef, usePlateSelectors } from '@udecode/plate-core';
import { Range, Path } from 'slate';

const getCursorOverlayStyles = props => createStyles({
  prefixClassNames: 'CursorOverlay',
  ...props
}, {
  selectionRect: [{
    "position": "absolute",
    "zIndex": "10",
    "pointerEvents": "none"
  }, css(["opacity:0.3;"])],
  caret: [{
    "position": "absolute",
    "zIndex": "10",
    "pointerEvents": "none"
  }, css(["width:2px;"])]
});

const Cursor = ({
  data,
  selectionRects,
  caretPosition,
  disableCaret,
  disableSelection,
  onRenderCaret: Caret,
  onRenderSelectionRect: Rect,
  ...props
}) => {
  if (!data) {
    return null;
  }

  const {
    caret,
    selectionRect
  } = getCursorOverlayStyles(props);
  const {
    style,
    selectionStyle = style
  } = data;
  return /*#__PURE__*/React.createElement(React.Fragment, null, !disableSelection && selectionRects.map((position, i) => Rect ? /*#__PURE__*/React.createElement(Rect, {
    key: i,
    data: data,
    selectionRect: position
  }) : /*#__PURE__*/React.createElement(_StyledDiv, {
    key: i,
    className: selectionRect === null || selectionRect === void 0 ? void 0 : selectionRect.className,
    style: { ...selectionStyle,
      ...position
    },
    $_css: selectionRect === null || selectionRect === void 0 ? void 0 : selectionRect.css
  })), !disableCaret && caretPosition && (Caret ? /*#__PURE__*/React.createElement(Caret, {
    data: data,
    caretPosition: caretPosition
  }) : /*#__PURE__*/React.createElement(_StyledDiv2, {
    className: caret === null || caret === void 0 ? void 0 : caret.className,
    style: { ...caretPosition,
      ...style
    },
    $_css2: caret === null || caret === void 0 ? void 0 : caret.css
  })));
};

var _StyledDiv = _styled("div").withConfig({
  displayName: "Cursor___StyledDiv",
  componentId: "sc-7354n0-0"
})(["", ""], p => p.$_css);

var _StyledDiv2 = _styled("div").withConfig({
  displayName: "Cursor___StyledDiv2",
  componentId: "sc-7354n0-1"
})(["", ""], p => p.$_css2);

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

const useRequestReRender = () => {
  const [, setUpdateCounter] = useState(0);
  const animationFrameRef = useRef(null);
  const requestReRender = useCallback((immediate = false) => {
    if (animationFrameRef.current && !immediate) {
      return;
    }

    if (!immediate) {
      animationFrameRef.current = requestAnimationFrame(() => {
        setUpdateCounter(state => state + 1);
        animationFrameRef.current = null;
      });
      return;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    setUpdateCounter(state => state + 1);
  }, []);
  useEffect(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  });
  useEffect(() => () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, []);
  return requestReRender;
};

const useRefreshOnResize = ({
  containerRef,
  refreshOnResize,
  selectionRectCache
}) => {
  const requestReRender = useRequestReRender(); // Reset the selection rect cache and request re-render.

  const refresh = useCallback((sync = false) => {
    selectionRectCache.current = new WeakMap();
    requestReRender(sync);
  }, [requestReRender, selectionRectCache]); // Refresh on container resize

  useEffect(() => {
    if (!refreshOnResize || !(containerRef !== null && containerRef !== void 0 && containerRef.current)) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => refresh());
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [containerRef, refresh, refreshOnResize]);
  return {
    refresh
  };
};

/**
 * Get the caret position of a range from selectionRects.
 */
const getCaretPosition = (selectionRects, range) => {
  const isCollapsed = range && Range.isCollapsed(range);
  const isBackward = range && Range.isBackward(range);
  const anchorRect = selectionRects[isBackward ? 0 : selectionRects.length - 1];

  if (!anchorRect) {
    return null;
  }

  return {
    height: anchorRect.height,
    top: anchorRect.top,
    left: anchorRect.left + (isBackward || isCollapsed ? 0 : anchorRect.width)
  };
};

/**
 * Get cursor overlay state from selection rects.
 */

const getCursorOverlayState = ({
  cursors: cursorStates,
  selectionRects
}) => {
  if (!cursorStates) return [];
  return Object.entries(cursorStates).map(([key, cursorState]) => {
    var _cursorState$selectio, _selectionRects$key;

    const selection = (_cursorState$selectio = cursorState === null || cursorState === void 0 ? void 0 : cursorState.selection) !== null && _cursorState$selectio !== void 0 ? _cursorState$selectio : null;
    const rects = (_selectionRects$key = selectionRects[key]) !== null && _selectionRects$key !== void 0 ? _selectionRects$key : FROZEN_EMPTY_ARRAY;
    const caretPosition = selection ? getCaretPosition(rects, selection) : null;
    return { ...cursorState,
      selection,
      caretPosition,
      selectionRects: rects
    };
  });
};

const getSelectionRects = (editor, {
  range,
  xOffset,
  yOffset
}) => {
  const [start, end] = Range.edges(range);
  const domRange = toDOMRange(editor, range);

  if (!domRange) {
    return [];
  }

  const selectionRects = [];
  const textEntries = getNodeEntries(editor, {
    at: range,
    match: isText
  });

  for (const [textNode, textPath] of textEntries) {
    const domNode = toDOMNode(editor, textNode);

    if (!domNode) {
      return [];
    }

    const isStartNode = Path.equals(textPath, start.path);
    const isEndNode = Path.equals(textPath, end.path);
    let clientRects = null;

    if (isStartNode || isEndNode) {
      const nodeRange = document.createRange();
      nodeRange.selectNode(domNode);

      if (isStartNode) {
        nodeRange.setStart(domRange.startContainer, domRange.startOffset);
      }

      if (isEndNode) {
        nodeRange.setEnd(domRange.endContainer, domRange.endOffset);
      }

      clientRects = nodeRange.getClientRects();
    } else {
      clientRects = domNode.getClientRects();
    }

    for (let i = 0; i < clientRects.length; i++) {
      const clientRect = clientRects.item(i);

      if (!clientRect) {
        continue;
      }

      selectionRects.push({
        width: clientRect.width,
        height: clientRect.height,
        top: clientRect.top - yOffset,
        left: clientRect.left - xOffset
      });
    }
  }

  return selectionRects;
};

const FROZEN_EMPTY_ARRAY = Object.freeze([]);
const useCursorOverlayPositions = ({
  containerRef,
  cursors: cursorStates,
  refreshOnResize = true
} = {}) => {
  const editor = useEditorRef();
  const selectionRectCache = useRef(new WeakMap());
  const [selectionRects, setSelectionRects] = useState({});
  const updateSelectionRects = useCallback(() => {
    // We have a container ref but the ref is null => container
    // isn't mounted to we can't calculate the selection rects.
    if (!(containerRef !== null && containerRef !== void 0 && containerRef.current)) return;
    if (!cursorStates) return;
    let xOffset = 0;
    let yOffset = 0;

    if (containerRef) {
      const contentRect = containerRef.current.getBoundingClientRect();
      xOffset = contentRect.x;
      yOffset = contentRect.y;
    }

    let selectionRectsChanged = Object.keys(selectionRects).length !== Object.keys(cursorStates).length;

    const getCachedSelectionRects = ({
      cursor
    }) => {
      const range = cursor.selection;

      if (!range) {
        return FROZEN_EMPTY_ARRAY;
      }

      const cached = selectionRectCache.current.get(range);

      if (cached) {
        return cached;
      }

      const rects = getSelectionRects(editor, {
        range,
        xOffset,
        yOffset
      });
      selectionRectsChanged = true;
      selectionRectCache.current.set(range, rects);
      return rects;
    };

    const updated = Object.fromEntries(Object.entries(cursorStates).map(([key, cursor]) => [key, getCachedSelectionRects({
      cursor
    })]));

    if (selectionRectsChanged) {
      setSelectionRects(updated);
    }
  }, [containerRef, cursorStates, editor, selectionRects]); // Update selection rects after paint
  // eslint-disable-next-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    updateSelectionRects();
  });
  const cursors = useMemo(() => getCursorOverlayState({
    selectionRects,
    cursors: cursorStates
  }), [cursorStates, selectionRects]);
  const {
    refresh
  } = useRefreshOnResize({
    containerRef,
    selectionRectCache,
    refreshOnResize
  });
  return {
    refresh,
    cursors
  };
};

const CursorOverlayContent = ({
  as,
  classNames,
  prefixClassNames,
  styles,
  onRenderCursor: CursorComponent = Cursor,
  onRenderSelectionRect,
  onRenderCaret,
  ...props
}) => {
  const {
    disableCaret,
    disableSelection
  } = props;
  const {
    cursors
  } = useCursorOverlayPositions(props);
  const cursorProps = {
    as,
    classNames,
    prefixClassNames,
    styles,
    onRenderSelectionRect,
    onRenderCaret,
    disableCaret,
    disableSelection
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, cursors.map(cursor => /*#__PURE__*/React.createElement(CursorComponent, _extends({
    key: cursor.key
  }, cursorProps, cursor))));
};
const CursorOverlay = props => {
  const isRendered = usePlateSelectors().isRendered();
  if (!isRendered) return null;
  return /*#__PURE__*/React.createElement(CursorOverlayContent, props);
};

export { Cursor, CursorOverlay, CursorOverlayContent, FROZEN_EMPTY_ARRAY, getCaretPosition, getCursorOverlayState, getCursorOverlayStyles, getSelectionRects, useCursorOverlayPositions, useRefreshOnResize, useRequestReRender };
//# sourceMappingURL=index.es.js.map
